/**
 * Mimu — make the cart's minus button remove the line when quantity is 1.
 *
 * Horizon disables the minus button once quantity reaches the line minimum
 * (component-cart-quantity-selector.js: `minusButton.disabled = value <= min`),
 * so shoppers press it, nothing happens, and they do not realise the separate
 * bin icon is the only way to remove the item. This re-enables it at quantity 1
 * and routes the press to that same bin button, so the last press just removes
 * the line — the standard behaviour people expect.
 *
 * Implemented by patching the custom element's prototype rather than by binding
 * to DOM nodes, because:
 *  - the cart drawer rebuilds its item list from the `sections=` HTML returned
 *    with every cart update, so any node-level listener would be thrown away;
 *  - a prototype patch is inherited by every instance the drawer ever renders,
 *    on both the drawer and the cart page, with nothing to re-attach.
 *
 * It defers entirely to the theme's own remove button, so removal follows the
 * exact same path as pressing the bin — no separate cart write, no new failure
 * mode. If a line cannot be removed (Horizon hides the bin for those), the
 * minus button is left disabled exactly as before.
 */
(function () {
  var TAG = 'cart-quantity-selector-component';

  /**
   * The bin button for this line, but only when it is genuinely usable.
   * Horizon renders it hidden when the line has can_remove == false.
   */
  function removeButtonFor(component) {
    var scope =
      component.closest('.cart-items__quantity-controls') ||
      component.closest('.cart-items__table-row') ||
      component.closest('tr');
    if (!scope) return null;

    var button = scope.querySelector('.cart-items__remove');
    if (!button || button.hidden || button.disabled) return null;
    return button;
  }

  function atMinimum(component) {
    var values = component.getCurrentValues();
    return values.value <= values.min;
  }

  customElements.whenDefined(TAG).then(function () {
    var Cls = customElements.get(TAG);
    if (!Cls || Cls.prototype.mimuMinusRemoves) return;
    Cls.prototype.mimuMinusRemoves = true;

    var originalUpdateButtonStates = Cls.prototype.updateButtonStates;
    var originalDecreaseQuantity = Cls.prototype.decreaseQuantity;

    /**
     * Let the theme set its states first, then re-enable minus at the minimum so
     * it can be pressed at all — a disabled button never fires a click event.
     */
    Cls.prototype.updateButtonStates = function () {
      originalUpdateButtonStates.call(this);

      var minusButton = this.refs && this.refs.minusButton;
      if (!minusButton) return;

      var removeButton = atMinimum(this) ? removeButtonFor(this) : null;

      if (!removeButton) {
        // Back to a plain decrement — drop anything this script added.
        minusButton.removeAttribute('data-mimu-removes');
        minusButton.removeAttribute('aria-label');
        return;
      }

      minusButton.disabled = false;
      minusButton.setAttribute('data-mimu-removes', 'true');

      // The button's visible label is a visually-hidden "Decrease quantity",
      // which would now be a lie for screen readers. Borrow the bin's label,
      // which already names the product.
      var label = removeButton.getAttribute('aria-label');
      if (label) minusButton.setAttribute('aria-label', label);
    };

    /**
     * At the minimum, hand the press to the bin button instead of clamping the
     * value and doing nothing.
     */
    Cls.prototype.decreaseQuantity = function (event) {
      if (atMinimum(this)) {
        var removeButton = removeButtonFor(this);
        if (removeButton) {
          if (event && typeof event.preventDefault === 'function') event.preventDefault();
          removeButton.click();
          return;
        }
      }

      return originalDecreaseQuantity.call(this, event);
    };

    // Instances already on the page ran the unpatched version during their
    // connectedCallback, so their minus button is still disabled. Re-run it.
    Array.prototype.forEach.call(document.querySelectorAll(TAG), function (el) {
      if (typeof el.updateButtonStates === 'function') el.updateButtonStates();
    });
  });
})();
